import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { Analytics } from '@vercel/analytics/react';

import { AuthProvider } from './context/AuthContext';
import Navigation from './components/Navigation';
import HomePage from './pages/HomePage';
import { CHART_REGISTRY } from './config/chartRegistry';
import SignIn from './pages/SignIn';
import SignUp from './pages/SignUp';
import Documentation from './guides/Documentation';
import PrivacyPolicy from './pages/PrivacyPolicy';
import './index.css';

import DataBlog from './datablog/DataBlog';
import BlogPost from './datablog/BlogPost';
import PageTracker from './utils/PageTracker';

// Never fire GA events from localhost / dev environments
const IS_PROD = window.location.hostname !== 'localhost'
  && window.location.hostname !== '127.0.0.1';

// Send page view config hit to GA4 on route changes
const trackPageView = (pathname) => {
  if (IS_PROD && window.gtag) {
    window.gtag('config', 'G-Q9M0QG5JY7', {
      page_path: pathname,
    });
  }
};

const PageTrackerComponent = () => {
  const location = useLocation();
  useEffect(() => {
    trackPageView(location.pathname);
  }, [location]);
  return null;
};

function App() {
  useEffect(() => {
    // Heartbeat: fires every 60 seconds to track active sessions.
    // Guarded by IS_PROD so local dev sessions never appear in GA4.
    if (!IS_PROD) return;

    const intervalId = setInterval(() => {
      if (window.gtag) {
        window.gtag('event', 'heartbeat', {
          event_category: 'User Engagement',
          event_label: 'Active Session',
        });
      }
    }, 60000);

    return () => clearInterval(intervalId);
  }, []);

  return (
    <HelmetProvider>
      <AuthProvider>
        <Router>
          <div className="App min-h-screen bg-gray-100">
            <meta name="version" content="2024.03.18.01" />
            <Navigation />
            <PageTrackerComponent />

            <div className="mx-auto w-full">
              <Routes>
                {/* Per-chart SEO routes — auto-generated from registry */}
                {CHART_REGISTRY.filter(e => e.seo).map(e => (
                  <Route key={e.chartType} path={`/${e.seo.slug}`}
                    element={<HomePage initialChartType={e.chartType} />} />
                ))}

                {/* All chart creation happens on the homepage via HomeChartStudio */}
                <Route path="/" element={<HomePage />} />

                {/* Public routes */}
                <Route path="/documentation" element={<Documentation />} />
                <Route path="/privacy"       element={<PrivacyPolicy />} />
                <Route path="/signin"        element={<SignIn />} />
                <Route path="/signup"        element={<SignUp />} />
                <Route path="/datablog"      element={<DataBlog />} />
                <Route path="/datablog/:slug" element={<BlogPost />} />
              </Routes>
            </div>

            <Analytics />
          </div>
        </Router>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;